import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { contactMailto } from "@/lib/contact";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import PublicHeader from "@/components/PublicHeader";
import StoryTour from "@/components/StoryTour";
import TransformationStory from "@/components/TransformationStory";
import { FounderStatement } from "@/components/FounderStatement";
import { ShareFlipDialog, fmtMoney } from "@/components/ShareFlipDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowRight, Vault, Lightning, SealCheck, Sparkle, Scales, Trophy, Tag,
  Confetti, Camera, ArrowUpRight, ShareNetwork, Crown, Lock, Armchair,
  ShieldCheck, Storefront, ChartLineUp, DownloadSimple, Target, Table, Users, Briefcase,
  CaretRight, PlayCircle,
} from "@phosphor-icons/react";

const SALE_IMG =
  "https://images.unsplash.com/photo-1652598631616-3f5f4d2cfbd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxmbGVhJTIwbWFya2V0JTIwYW50aXF1ZSUyMGZpbmR8ZW58MHx8fHwxNzgyODcwODMwfDA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { icon: Vault, title: "Restore ownership", body: "Your descriptions, dimensions and photos — the hours you've already spent — saved once and owned by you. Not the marketplace." },
  { icon: Lightning, title: "Cross-list without re-typing", body: "Translate a single record into each marketplace's format — export-ready for the platforms you choose, no re-typing." },
  { icon: SealCheck, title: "Nothing slips through", body: "A guided, you-approve-it review so a dimension, photo or detail is never missed before you list it." },
];

// The complete workflow — every capability, grouped into 3 scannable themes.
const FEATURE_GROUPS = [
  {
    group: "Hunt Like a Pro",
    items: [
      { copy: "Instant photo ID & era match", Icon: Camera, testid: "feat-hunt-photo-id" },
      { copy: "Spot fakes before you buy", Icon: ShieldCheck, testid: "feat-hunt-spot-fakes" },
      { copy: "See best-selling marketplaces", Icon: Storefront, testid: "feat-hunt-best-fit" },
      { copy: "Check live market prices", Icon: ChartLineUp, testid: "feat-hunt-live-prices" },
      { copy: "Send acquisitions straight to Drafts", Icon: Lightning, testid: "feat-hunt-to-drafts" },
    ],
  },
  {
    group: "List in Minutes",
    items: [
      { copy: "Import once, reuse it wherever you sell", Icon: DownloadSimple, testid: "feat-list-import" },
      { copy: "AI descriptions in your voice", Icon: Sparkle, testid: "feat-list-ai-desc" },
      { copy: "Cross-list from one master record", Icon: ShareNetwork, testid: "feat-list-cross-list" },
      { copy: "Track & update prices in bulk", Icon: Tag, testid: "feat-list-bulk-price" },
      { copy: "Export listings & photos anytime", Icon: Vault, testid: "feat-list-export" },
      { copy: "Reroute rejected items instantly", Icon: ArrowUpRight, testid: "feat-list-reroute" },
    ],
  },
  {
    group: "Sell Smarter",
    items: [
      { copy: "Data-driven pricing strategies", Icon: Scales, testid: "feat-sell-pricing" },
      { copy: "Find your item's perfect market", Icon: Target, testid: "feat-sell-placement" },
      { copy: "Compare marketplace fees & rules", Icon: Table, testid: "feat-sell-compare" },
      { copy: "Track profits on the Scoreboard", Icon: Trophy, testid: "feat-sell-scoreboard" },
      { copy: "View community pricing insights", Icon: Users, testid: "feat-sell-community" },
      { copy: "Own & export your portfolio", Icon: Briefcase, testid: "feat-sell-portfolio" },
    ],
  },
];

const DONT_DO = [
  "We don't guarantee marketplace approval — curated platforms like 1stDibs & Chairish decide who gets in.",
  "We don't handle shipping or handling — that stays with the marketplace you sell on.",
  "We don't promise a sale — we widen your reach; the market decides the rest.",
];

// THE LOOP — canonical 6 steps (BRAND.md), rendered natively with the seller's compass icons.
const LOOP = [
  { key: "create", n: "01", title: "Create", body: "Capture your find and build your draft in seconds." },
  { key: "enhance", n: "02", title: "Enhance", body: "AI fills in the details — you review, refine and approve." },
  { key: "list", n: "03", title: "List", body: "Choose your marketplaces and get every piece ready to publish." },
  { key: "track", n: "04", title: "Track", body: "Monitor prices and performance across all your listings." },
  { key: "keep", n: "05", title: "Keep", body: "Your inventory. Your data. Your freedom." },
  { key: "crosslist", n: "06", title: "Cross-list", body: "One master record, export-ready for the marketplaces you choose." },
];
// 6 evenly-spaced points on a circle (start top, clockwise) for the lg ring layout.
const LOOP_POS = [{ x: 50, y: 6 }, { x: 88, y: 28 }, { x: 88, y: 72 }, { x: 50, y: 94 }, { x: 12, y: 72 }, { x: 12, y: 28 }];
// Directional arrowheads sitting on the ring, midway between steps, rotated clockwise (tangent).
const LOOP_ARROWS = [
  { x: 73, y: 12, r: 30 }, { x: 94, y: 50, r: 90 }, { x: 73, y: 88, r: 150 },
  { x: 27, y: 88, r: 210 }, { x: 6, y: 50, r: 270 }, { x: 27, y: 12, r: 330 },
];

// The seller's journey — the six pillars.
const HERO_BULLETS = [
  { label: "Export your work", desc: "upload even a limited marketplace export and Vintage Vantage rebuilds a complete, seller-owned inventory" },
  { label: "Source smarter", desc: "know what you've found before you buy — identify, verify, research, purchase with confidence" },
  { label: "Draft instantly", desc: "we pre-fill what we can verify; you add what only you know — final approval is always yours" },
  { label: "Build once", desc: "create one seller-owned record for every item, and reuse it wherever you sell" },
  { label: "Cross-list", desc: "cross-list to supported marketplaces from one seller-owned record — one inventory, multiple marketplaces" },
  { label: "Compounded capability", desc: "tiered market intelligence that helps you make better decisions over time" },
];

const FINDS_JOURNEY = ["Take a picture", "Identify", "Verify", "Research", "Purchase with confidence"];

const SOURCES = ["Flea Market", "Goodwill / Thrift Store", "Estate Sale", "Swap Meet", "Yard Sale", "Auction", "Other"];

function SubmitFlipDialog({ open, onClose, onSubmitted }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ seller_name: "", title: "", source: SOURCES[0], found_price: "", sold_price: "", story: "", image_url: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && user) setForm((f) => ({ ...f, seller_name: f.seller_name || user.name || "" }));
  }, [open, user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/flip-stories", {
        ...form,
        found_price: parseFloat(form.found_price) || 0,
        sold_price: parseFloat(form.sold_price) || 0,
      });
      toast.success("Your flip is in the running — good luck! 🏆");
      onSubmitted?.();
      onClose();
      setForm({ seller_name: user?.name || "", title: "", source: SOURCES[0], found_price: "", sold_price: "", story: "", image_url: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't submit your flip");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="submit-flip-dialog">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 mb-1"><Trophy size={20} weight="fill" /> <span className="label-caps">Sale of the Month</span></div>
          <DialogTitle className="font-serif-display text-3xl font-bold tracking-tight">Enter your best flip</DialogTitle>
          <DialogDescription className="text-[#525252]">The winning story earns <strong className="text-[#0D1B2A]">free Premium membership</strong> — unlimited cross-listings, on the house.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-1">
          <input data-testid="flip-title-input" required value={form.title} onChange={set("title")} placeholder="Headline — e.g. $5 sideboard → $540" className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
          <div className="grid grid-cols-3 gap-3">
            <select data-testid="flip-source-select" value={form.source} onChange={set("source")} className="h-12 border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input data-testid="flip-found-input" type="number" min="0" step="0.01" required value={form.found_price} onChange={set("found_price")} placeholder="Paid $" className="h-12 border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
            <input data-testid="flip-sold-input" type="number" min="0" step="0.01" required value={form.sold_price} onChange={set("sold_price")} placeholder="Sold $" className="h-12 border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
          </div>
          <textarea data-testid="flip-story-input" required rows={4} value={form.story} onChange={set("story")} placeholder="Tell us the story of the find and the sale…" className="w-full border border-[#0D1B2A]/30 rounded-none px-3 py-2 text-[16px] focus:border-[#0D1B2A] outline-none resize-none" />
          <input data-testid="flip-image-input" value={form.image_url} onChange={set("image_url")} placeholder="Photo URL (optional)" className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
          <button data-testid="flip-submit-button" type="submit" disabled={busy} className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-50">
            {busy ? "Submitting…" : "Submit my flip"} <ArrowRight size={18} weight="bold" />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FlipCard({ s, testid, onShare }) {
  return (
    <div data-testid={testid} className="border border-[#0D1B2A]/20 bg-[#FDFBF7] overflow-hidden group">
      {s.image_url && (
        <div className="aspect-[4/3] overflow-hidden bg-[#F5F3ED]">
          <img src={s.image_url} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide bg-[#0D1B2A] text-[#FDFBF7] px-2 py-1"><Tag size={12} weight="fill" /> {s.source}</span>
          {s.multiple ? <span className="text-xs font-bold text-[#B47A3C]">{s.multiple}× return</span> : null}
        </div>
        <p className="font-serif-display text-2xl font-bold leading-tight mb-2">{s.title}</p>
        <p className="text-[15px] text-[#525252] leading-relaxed line-clamp-3">{s.story}</p>
        <div className="flex items-center justify-between mt-3">
          <p className="text-sm font-semibold text-[#8A867D]">— {s.seller_name}</p>
          <button onClick={() => onShare(s)} data-testid={`${testid}-share`} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:text-[#B47A3C] transition-colors"><ShareNetwork size={15} weight="fill" /> Share</button>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [featured, setFeatured] = useState(null);
  const [stories, setStories] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);
  const [shareStory, setShareStory] = useState(null);
  const [community, setCommunity] = useState(null);
  const [founding, setFounding] = useState(null);
  const [showTour, setShowTour] = useState(false);

  const loadFlips = () => {
    api.get("/flip-stories").then(({ data }) => {
      setFeatured(data.featured || null);
      setStories(data.stories || []);
    }).catch(() => {});
  };
  useEffect(loadFlips, []);
  useEffect(() => {
    api.get("/community/stats").then(({ data }) => setCommunity(data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setFounding(data)).catch(() => {});
  }, []);

  const foundingLeft = founding ? Math.max(0, founding.founding_spots_total - founding.founding_spots_taken) : null;

  // Open the tour from a ?tour=1 link (works even when already on Landing), then clean the URL.
  useEffect(() => {
    if (searchParams.get("tour") === "1") {
      setShowTour(true);
      searchParams.delete("tour");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const startCta = () => navigate(user ? "/dashboard" : "/register");
  const openSubmit = () => {
    if (!user) { toast.message("Sign in to enter the Sale of the Month"); navigate("/login"); return; }
    setShowSubmit(true);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      <PublicHeader />

      {/* HERO — copy left, compass loop right (the hero visual) */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-16 grid lg:grid-cols-2 gap-10 items-center" data-testid="landing-hero">
        <div className="vv-fade-up">
          <p className="label-caps text-center mb-3" style={{ color: "#B47A3C", letterSpacing: "0.18em" }}>YOUR inventory · YOUR decision · YOUR vision</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] font-medium mb-5">
            List your finds.<br /><span className="italic text-[#B47A3C]">In record time.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#4A4A4A] leading-relaxed max-w-xl mb-6" data-testid="hero-mission-line">
            Vintage Vantage doesn't just <span className="font-semibold text-[#0D1B2A]">save time</span>. It <span className="font-semibold text-[#B47A3C]">compounds your capability</span>.
          </p>
          <p className="label-caps mb-4" style={{ color: "#B47A3C", letterSpacing: "0.16em" }}>The first seller-intelligence layer for the vintage market</p>
          <ul className="space-y-2.5 mb-8" data-testid="hero-bullets">
            {HERO_BULLETS.map((b) => (
              <li key={b.label} className="flex items-start gap-3">
                <span className="mt-[8px] shrink-0 w-2 h-2 rotate-45 bg-[#B47A3C]" />
                <span className="text-[15px] leading-snug">
                  <strong className="text-[#0D1B2A] font-semibold">{b.label}</strong> <span className="text-[#525252]">— {b.desc}</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mb-6" data-testid="hero-supported-platforms">
            <span className="text-[13px] font-bold uppercase tracking-wide text-[#8A867D]">Cross-listing today:</span>
            {["Chairish", "1stDibs", "AptDeco", "Pamono"].map((p) => (
              <span key={p} className="inline-flex items-center h-7 px-2.5 border border-[#0D1B2A]/25 bg-white text-[13px] font-semibold text-[#0D1B2A]">{p}</span>
            ))}
            <button onClick={() => navigate("/founding")} data-testid="hero-more-platforms-link" className="text-[13px] text-[#B47A3C] italic font-semibold underline underline-offset-4 decoration-[#B47A3C]/40 hover:text-[#0D1B2A] transition-colors">— more to come</button>
          </div>
          {foundingLeft != null && foundingLeft > 0 && (
            <button
              onClick={() => navigate("/plans")}
              data-testid="hero-founding-scarcity"
              className="group mb-6 inline-flex items-center gap-2.5 border border-[#B47A3C]/50 bg-[#B47A3C]/10 hover:bg-[#B47A3C]/15 rounded-full pl-3 pr-4 py-1.5 transition-colors"
            >
              <Crown size={16} weight="fill" className="text-[#B47A3C]" />
              <span className="text-sm font-semibold text-[#0D1B2A]">
                Only <span className="text-[#B47A3C]">{foundingLeft} of {founding.founding_spots_total}</span> Founding Council seats left
              </span>
              <span className="hidden sm:inline text-xs text-[#8A6A2F]">· $29/mo for life</span>
              <ArrowRight size={14} weight="bold" className="text-[#B47A3C] transition-transform group-hover:translate-x-0.5" />
            </button>
          )}
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={startCta} data-testid="hero-get-started" className="h-14 px-8 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-95">
              {user ? "Go to dashboard" : "Get started free"} <ArrowRight size={18} weight="bold" />
            </button>
            <button onClick={() => setShowTour(true)} data-testid="hero-watch-story" className="h-14 px-7 border-2 border-[#0D1B2A] text-[#0D1B2A] rounded-none font-medium text-[17px] inline-flex items-center gap-2 hover:bg-[#0D1B2A] hover:text-[#FDFBF7] transition-colors active:scale-95">
              <PlayCircle size={22} weight="fill" className="text-[#B47A3C]" /> Watch the story
            </button>
          </div>
        </div>

        {/* Compass loop — the hero visual */}
        <div className="relative vv-fade-up" style={{ animationDelay: "0.15s" }} data-testid="hero-loop">
          {/* Ring — lg and up */}
          <div className="hidden lg:block relative mx-auto" style={{ width: 440, height: 440 }} data-testid="loop-ring">
            <div className="absolute rounded-full border-2 border-dashed border-[#B47A3C]/35" style={{ inset: "9%" }} />
            {LOOP_ARROWS.map((a, i) => (
              <div key={i} className="absolute" style={{ left: `${a.x}%`, top: `${a.y}%`, transform: `translate(-50%,-50%) rotate(${a.r}deg)` }}>
                <CaretRight size={22} weight="bold" className="text-[#B47A3C]" />
              </div>
            ))}
            <img src="/loop/compass.png" alt="Vintage Vantage compass" className="absolute left-1/2 top-1/2 w-36 h-36 object-contain opacity-95" style={{ transform: "translate(-50%,-50%)" }} />
            {LOOP.map((s, i) => (
              <div
                key={s.key}
                data-testid={`loop-step-${s.key}`}
                className="absolute text-center group"
                style={{ left: `${LOOP_POS[i].x}%`, top: `${LOOP_POS[i].y}%`, transform: "translate(-50%,-50%)" }}
              >
                <div className="w-20 h-20 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <img src={`/loop/${s.key}.png`} alt={s.title} className="w-20 h-20 object-contain" />
                </div>
                <p className="mt-1.5 text-[12px] font-bold uppercase tracking-[0.12em] text-[#0D1B2A] transition-colors group-hover:text-[#B47A3C]">{s.title}</p>
              </div>
            ))}
          </div>
          {/* Compact icon grid — below lg */}
          <div className="lg:hidden grid grid-cols-3 gap-x-4 gap-y-6" data-testid="hero-loop-mobile">
            {LOOP.map((s) => (
              <div key={s.key} className="text-center">
                <img src={`/loop/${s.key}.png`} alt={s.title} className="w-16 h-16 object-contain mx-auto" />
                <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.1em] text-[#0D1B2A]">{s.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FANTASTIC FINDS — know what you've found before you buy */}
      <section className="max-w-6xl mx-auto px-6 pt-4 pb-16" data-testid="finds-teaser">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Camera size={20} weight="fill" className="text-[#B47A3C]" />
              <span className="label-caps">Fantastic Finds · on the hunt</span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05] font-medium mb-4">
              Know what you've found —<br /><span className="italic text-[#B47A3C]">before you buy.</span>
            </h2>
            <p className="text-lg text-[#525252] leading-relaxed mb-5">
              Take a photo while sourcing. Vintage Vantage identifies likely makers, era, comparable
              examples, and where the piece is most likely to succeed.
            </p>
            <div className="flex flex-wrap items-center gap-y-2 mb-5" data-testid="finds-journey">
              {FINDS_JOURNEY.map((step, i) => (
                <span key={step} className="inline-flex items-center">
                  <span className="text-[15px] font-semibold text-[#0D1B2A]">{step}</span>
                  {i < FINDS_JOURNEY.length - 1 && <CaretRight size={16} weight="bold" className="text-[#B47A3C] mx-2" />}
                </span>
              ))}
            </div>
            <p className="text-[16px] text-[#33302B] leading-relaxed mb-7">
              When purchased — <strong className="text-[#0D1B2A]">one tap</strong> sends the verified information into
              Drafts, waiting in your inventory.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => navigate("/register")} data-testid="finds-teaser-cta"
                className="bg-[#0D1B2A] text-[#FDFBF7] h-12 px-6 rounded-none font-medium inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-95">
                Start hunting free <ArrowRight size={16} weight="bold" />
              </button>
              <button onClick={() => navigate("/finds/community")} data-testid="finds-teaser-gallery"
                className="h-12 px-6 rounded-none font-medium border border-[#0D1B2A] hover:bg-[#F5F3ED] transition-colors inline-flex items-center gap-2">
                <Sparkle size={16} weight="fill" /> See the finds gallery
              </button>
            </div>
          </div>
          <div className="rounded-2xl border border-[#0D1B2A] bg-white p-6 shadow-sm">
            {/* Silhouette header — stand-in until we drop a real dealer photo here */}
            <div className="mb-4 h-32 rounded-lg bg-gradient-to-br from-[#F5F1E8] to-[#E7E0CE] border border-[#E5E0D6] flex items-center justify-center" data-testid="finds-demo-silhouette">
              <Armchair size={72} weight="duotone" className="text-[#B47A3C]" />
            </div>
            <div className="inline-flex items-center gap-1.5 bg-[#0D1B2A] text-[#FDFBF7] text-xs font-bold px-2.5 py-1 rounded-full mb-4">
              <Sparkle size={13} weight="fill" /> Best home: Chairish · 100% accepted for pieces like this
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A867D]">Era</p>
                <p className="text-2xl font-extrabold tracking-tight text-[#0D1B2A]">1970s</p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A867D]">Possible makers</p>
                <p className="text-base font-bold text-[#0D1B2A] leading-tight">Milo Baughman, Thayer Coggin</p>
              </div>
            </div>
            <div className="mt-5 rounded-lg bg-[#FBFAF6] border border-[#E7E3DA] p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A867D] mb-2">See what it's selling for</p>
              <div className="flex flex-wrap gap-2">
                {["Chairish", "1stDibs", "Pamono", "AptDeco"].map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 h-8 px-3 border border-[#0D1B2A] rounded-md text-sm font-semibold">
                    {p} <ArrowUpRight size={13} weight="bold" />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEE IT IN ACTION — before/after inventory recovery + the seller's journey */}
      <TransformationStory />

      {/* VALUE BAND — the philosophy: seller ownership */}
      <section className="max-w-6xl mx-auto px-6 py-8" data-testid="value-band">
        <div className="relative overflow-hidden vv-grain bg-[#0D1B2A] text-[#FDFBF7] px-8 sm:px-12 py-14 sm:py-16">
          <div className="relative">
            <p className="label-caps mb-3" style={{ color: "#B47A3C" }}>Turn your passion into a profession</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] font-medium max-w-4xl">
              Why recreate the same knowledge <span className="italic text-[#B47A3C]">every time you sell?</span>
            </h2>
            <p className="text-lg text-[#D8D3C8] leading-relaxed max-w-2xl mt-5">
              Marketplace listings are temporary. Seller knowledge is permanent. The research, the measurements,
              the words — saved once and working for you wherever you sell.
            </p>
            <button onClick={startCta} data-testid="value-band-cta" className="mt-8 h-14 px-8 bg-[#FDFBF7] text-[#0D1B2A] rounded-none font-medium text-[17px] inline-flex items-center gap-2 hover:bg-white transition-transform active:scale-95">
              {user ? "Go to dashboard" : "Start free"} <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* ONE LOOP — the Knowledge Flywheel */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="loop-section">
        <div className="text-center mb-10">
          <span className="label-caps" style={{ color: "#B47A3C", letterSpacing: "0.2em" }}>A compass for your business</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium mt-3">
            One loop. Every piece, <span className="italic text-[#B47A3C]">from find to forever.</span>
          </h2>
          <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mx-auto mt-5" data-testid="flywheel-philosophy">
            Every verified decision reduces unnecessary effort in the future — turning today's work into
            tomorrow's reusable knowledge.
          </p>
          <p className="font-serif-display text-2xl sm:text-3xl text-[#0D1B2A] mt-5">
            The result isn't smarter AI. <span className="italic text-[#B47A3C]">It's smarter workflows.</span>
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <div className="border-2 border-[#0D1B2A] bg-[#FDFBF7] p-7" data-testid="flywheel-experienced">
            <div className="h-11 w-11 bg-[#0D1B2A] flex items-center justify-center mb-4">
              <ChartLineUp size={22} color="#B47A3C" weight="fill" />
            </div>
            <h3 className="font-serif-display text-2xl font-bold text-[#0D1B2A] mb-1.5">Experienced sellers</h3>
            <p className="text-[16px] text-[#525252] leading-relaxed">Scale with less repeated work.</p>
          </div>
          <div className="border-2 border-[#0D1B2A] bg-[#FDFBF7] p-7" data-testid="flywheel-new">
            <div className="h-11 w-11 bg-[#0D1B2A] flex items-center justify-center mb-4">
              <Sparkle size={22} color="#B47A3C" weight="fill" />
            </div>
            <h3 className="font-serif-display text-2xl font-bold text-[#0D1B2A] mb-1.5">New sellers</h3>
            <p className="text-[16px] text-[#525252] leading-relaxed">Get guidance from the beginning.</p>
          </div>
        </div>
      </section>

      {/* SALE OF THE MONTH */}
      <section className="bg-[#F5F3ED] border-y border-[#0D1B2A]/15 py-20" data-testid="sale-of-month">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 mb-3"><Trophy size={22} weight="fill" className="text-[#B47A3C]" /><span className="label-caps">Sale of the Month</span></div>
              <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-tight font-medium max-w-2xl">The thrill of the hunt, rewarded.</h2>
            </div>
            <div className="bg-[#0D1B2A] text-[#FDFBF7] px-5 py-4 max-w-xs">
              <div className="flex items-center gap-2 mb-1"><Confetti size={18} weight="fill" className="text-[#B47A3C]" /><span className="label-caps" style={{ color: "#FDFBF7" }}>The prize</span></div>
              <p className="text-[15px] leading-snug">Each month's winner is bumped to <strong>free Premium</strong> — unlimited cross-listings, on us.</p>
            </div>
          </div>

          {featured && (
            <div className="grid lg:grid-cols-2 gap-10 items-center mb-12" data-testid="featured-flip">
              <div className="relative vv-grain overflow-hidden border border-[#0D1B2A]/20 order-2 lg:order-1">
                <img src={featured.image_url || SALE_IMG} alt={featured.title} className="w-full h-[420px] object-cover" />
                <div className="absolute bottom-5 right-5 bg-[#FDFBF7] border border-[#0D1B2A] px-4 py-3 shadow-[5px_5px_0_0_rgba(10,10,10,1)]">
                  <p className="label-caps mb-0.5">Paid → Sold</p>
                  <p className="font-serif-display text-2xl font-bold leading-none">{fmtMoney(featured.found_price)} → {fmtMoney(featured.sold_price)}</p>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-[#B47A3C] text-white px-3 py-1.5 mb-4">
                  <Sparkle size={13} weight="fill" /> This month's winner
                </span>
                <h3 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-4">{featured.title}</h3>
                <p className="text-lg text-[#525252] leading-relaxed mb-5">{featured.story}</p>
                <p className="font-serif-display text-2xl italic text-[#0D1B2A] border-l-2 border-[#B47A3C] pl-4">— {featured.seller_name}</p>
                <button onClick={() => setShareStory(featured)} data-testid="featured-share-button" className="mt-6 h-12 px-6 border border-[#0D1B2A] rounded-none font-medium inline-flex items-center gap-2 hover:bg-[#F5F3ED] transition-colors">
                  <ShareNetwork size={18} weight="fill" /> Share this flip
                </button>
              </div>
            </div>
          )}

          {stories.length > 0 && (
            <>
              <p className="label-caps mb-4">Recent finds from the community</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" data-testid="flip-gallery">
                {stories.slice(0, 3).map((s, i) => <FlipCard key={s.id} s={s} testid={`flip-card-${i}`} onShare={setShareStory} />)}
              </div>
            </>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <button onClick={openSubmit} data-testid="submit-flip-button" className="h-14 px-8 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-95">
              <Camera size={18} weight="fill" /> Submit your flip
            </button>
            <span className="text-[15px] text-[#525252]">$5 at the swap meet → $500 at the sale? We want to hear it.</span>
          </div>
        </div>
      </section>

      {/* COMMUNITY AGGREGATE — opt-in, anonymized, above a k-anonymity threshold */}
      {community?.available && (
        <section className="bg-[#0D1B2A] text-[#FDFBF7] py-20" data-testid="community-stats">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="label-caps" style={{ color: "#B47A3C" }}>The Vantage community, in aggregate</span>
              <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight font-medium mt-2">Pooled results from sellers like you.</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Sellers contributing", value: community.sellers },
                { label: "Pieces sold", value: community.sales },
                { label: "Total flipped", value: fmtMoney(community.total_revenue) },
                { label: "Avg return", value: community.avg_multiple != null ? `${community.avg_multiple}×` : "—" },
              ].map((s) => (
                <div key={s.label} className="border border-white/10 bg-white/5 p-6 text-center">
                  <p className="font-serif-display text-4xl font-bold leading-none">{s.value}</p>
                  <p className="label-caps mt-2" style={{ color: "#B8B8B8" }}>{s.label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-8 text-[15px] text-[#D8D3C8]">
              {community.best_multiple ? <span><span className="text-[#B47A3C] font-bold">{community.best_multiple}×</span> best return {community.best_item ? `(a ${community.best_item})` : ""}</span> : null}
              {community.avg_days_to_sale != null ? <span><span className="font-bold text-[#FDFBF7]">{community.avg_days_to_sale} days</span> avg to sell</span> : null}
              {community.top_platforms?.[0] ? <span>Most-used: <span className="font-bold text-[#FDFBF7]">{community.top_platforms[0].platform}</span></span> : null}
            </div>
            <div className="flex justify-center mt-8">
              {founding && (
                <button
                  onClick={() => navigate("/plans")}
                  data-testid="community-founding-counter"
                  className="group inline-flex items-center gap-2.5 border border-[#B47A3C]/50 bg-[#B47A3C]/15 hover:bg-[#B47A3C]/25 rounded-full pl-3 pr-4 py-2 transition-colors"
                >
                  <Crown size={16} weight="fill" className="text-[#B47A3C]" />
                  {founding.founding_spots_taken > 0 ? (
                    <span className="text-sm font-semibold text-[#FDFBF7]">
                      🎉 <span className="text-[#B47A3C]">Seat #{founding.founding_spots_taken} claimed</span> — {Math.max(0, founding.founding_spots_total - founding.founding_spots_taken)} of {founding.founding_spots_total} remain
                      {founding.founding_claimed_this_week >= 2 && (
                        <span data-testid="founding-momentum" className="text-[#B47A3C]"> · {founding.founding_claimed_this_week} this week</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-sm font-semibold text-[#FDFBF7]">
                      {founding.founding_spots_total} Founding Council seats open — <span className="text-[#B47A3C]">be Seat #1</span>
                    </span>
                  )}
                  <span className="hidden sm:inline text-xs text-[#D8D3C8]">· $29/mo for life</span>
                  <ArrowRight size={14} weight="bold" className="text-[#B47A3C] transition-transform group-hover:translate-x-0.5" />
                </button>
              )}
            </div>
            <p className="text-center text-sm text-[#8A867D] mt-8 max-w-2xl mx-auto">
              Opt-in, anonymized and aggregated — individual listings are never shown, and any seller can switch it off anytime.
            </p>
          </div>
        </section>
      )}

      {/* CLOSING — the complete workflow, condensed into 3 themes */}
      <section className="max-w-7xl mx-auto px-6 py-16 sm:py-20" data-testid="features-section">
        <div className="mb-12">
          <span className="label-caps" style={{ color: "#B47A3C", letterSpacing: "0.2em" }}>The complete workflow</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-medium mt-3 max-w-3xl">
            Stop repeating yourself. <span className="italic text-[#B47A3C]">Start selling more.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-stretch">
          {FEATURE_GROUPS.map((g, gi) => (
            <div
              key={g.group}
              data-testid={`feature-group-${gi}`}
              className="vv-fade-up flex flex-col bg-[#FDFBF7] border-2 border-[#0D1B2A] p-8 md:p-9 rounded-none shadow-[8px_8px_0_0_rgba(10,10,10,1)] transition-transform duration-300 ease-out hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[12px_12px_0_0_rgba(10,10,10,1)]"
              style={{ animationDelay: `${gi * 0.12}s` }}
            >
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#0D1B2A] mb-7 pb-5 border-b-2 border-[#E7E3DA]">{g.group}</h3>
              <ul className="flex flex-col gap-5 flex-grow">
                {g.items.map((it) => (
                  <li key={it.testid} data-testid={it.testid} className="flex items-start gap-4 group">
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-[#B47A3C] transition-transform duration-200 group-hover:scale-110">
                      <it.Icon size={26} weight="fill" />
                    </span>
                    <span className="text-[16px] md:text-[17px] text-[#0D1B2A] font-medium leading-snug pt-1 transition-colors duration-200 group-hover:text-[#B47A3C]">{it.copy}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* FOUNDING COUNCIL */}
      <section id="founding" className="max-w-6xl mx-auto px-6 pb-20" data-testid="founding-band">
        <div className="border-2 border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] p-8 sm:p-12">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-3">
                <Crown size={22} weight="fill" className="text-[#B47A3C]" />
                <span className="label-caps" style={{ color: "#E5E5E5" }}>The Founding Vision · limited cohort</span>
              </div>
              <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-tight font-medium mb-4">
                Help shape it. Keep the perks <span className="italic text-[#B47A3C]">for life.</span>
              </h2>
              <p className="text-[17px] text-[#E5E5E5] leading-relaxed mb-6 max-w-xl">
                We're inviting a small founding cohort to steer the roadmap. No payment now — we want your
                feedback first. In return you lock in founding pricing and lifetime perks.
              </p>
              <ul className="space-y-2.5 mb-8">
                {[
                  { icon: Crown, t: "Lifetime Founding Council badge" },
                  { icon: Lock, t: "Locked-in introductory pricing" },
                  { icon: Lightning, t: "Early access + a direct say on the roadmap" },
                ].map((f) => (
                  <li key={f.t} className="flex items-center gap-3 text-[16px]">
                    <f.icon size={18} weight="fill" className="text-[#B47A3C] shrink-0" /> <span>{f.t}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/founding")}
                data-testid="founding-band-cta"
                className="h-14 px-8 bg-[#FDFBF7] text-[#0D1B2A] rounded-none font-medium text-[17px] inline-flex items-center gap-2 hover:bg-white transition-transform active:scale-[0.98]"
              >
                Claim your Founding Council seat <ArrowRight size={18} weight="bold" />
              </button>
            </div>
            <div className="hidden lg:flex items-center justify-center">
              <div className="border border-[#FDFBF7]/20 bg-white/[0.03] p-10 text-center w-full max-w-sm">
                <div className="h-20 w-20 rounded-full bg-[#B47A3C] flex items-center justify-center mx-auto mb-5">
                  <Crown size={40} weight="fill" className="text-[#FDFBF7]" />
                </div>
                <p className="font-serif-display text-2xl font-bold mb-1">Founding Council</p>
                <p className="text-[15px] text-[#B8B8B8]">A permanent badge — you were here first.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SELLER ADVANTAGES — moved lower, linked from the footer */}
      <section id="advantages" className="max-w-6xl mx-auto px-6 pb-20" data-testid="value-pillars">
        <div className="text-center mb-10">
          <span className="label-caps">Why sellers switch</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-tight font-medium mt-2">
            Seller ad<span className="text-[#B47A3C]">V</span>antages
          </h2>
        </div>
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-8 border border-[#0D1B2A]/20 bg-white p-10 hover:bg-[#F5F3ED] transition-colors">
            <div className="h-12 w-12 bg-[#0D1B2A] flex items-center justify-center mb-5">
              <Vault size={24} color="#FDFBF7" weight="fill" />
            </div>
            <h3 className="font-serif-display text-3xl font-bold mb-3">{PILLARS[0].title}</h3>
            <p className="text-lg text-[#525252] leading-relaxed max-w-xl">{PILLARS[0].body}</p>
          </div>
          <div className="md:col-span-4 grid grid-rows-2 gap-6">
            {PILLARS.slice(1).map((p) => (
              <div key={p.title} className="border border-[#0D1B2A]/20 bg-white p-7 hover:bg-[#F5F3ED] transition-colors">
                <div className="h-10 w-10 bg-[#0D1B2A] flex items-center justify-center mb-3">
                  <p.icon size={20} color="#FDFBF7" weight="fill" />
                </div>
                <h3 className="font-serif-display text-2xl font-bold mb-1.5">{p.title}</h3>
                <p className="text-[15px] text-[#525252] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER'S STATEMENT — the closing word before the footer */}
      <FounderStatement />

      {/* FOOTER */}
      <footer className="border-t border-[#0D1B2A]/15 bg-[#0D1B2A] text-[#FDFBF7]">
        {/* What we don't do — clear expectations, on purpose */}
        <div className="max-w-6xl mx-auto px-6 pt-12" data-testid="footer-dont-do">
          <p className="text-sm font-bold uppercase tracking-wide text-[#B47A3C] mb-4">What we don't do</p>
          <ul className="grid sm:grid-cols-3 gap-x-8 gap-y-4">
            {DONT_DO.map((x) => (
              <li key={x} className="flex items-start gap-3 text-[15px] leading-snug text-[#D8D3C8]">
                <span className="h-2 w-2 mt-2 bg-[#8A867D] shrink-0" /> <span>{x}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="max-w-6xl mx-auto px-6 py-12 mt-8 border-t border-[#FDFBF7]/10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/vv_logo.jpg" alt="Vintage Vantage seal" className="h-11 w-11 rounded-full object-cover ring-1 ring-[#FDFBF7]/20" />
            <div>
              <p className="font-serif-display text-xl font-bold leading-none">Vintage Vantage</p>
              <p className="text-sm text-[#B8B8B8] mt-1">Own your work. Sell where you choose.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[15px]">
            <a href="/#advantages" data-testid="footer-advantages-link" className="hover:text-[#B47A3C] transition-colors">Seller adVantages</a>
            <button onClick={() => navigate("/faq")} data-testid="footer-faq-link" className="hover:text-[#B47A3C] transition-colors">FAQ</button>
            <button onClick={() => navigate("/guide")} data-testid="footer-guide-link" className="hover:text-[#B47A3C] transition-colors">Compare platforms</button>
            <button onClick={() => navigate("/terms")} data-testid="footer-terms-link" className="hover:text-[#B47A3C] transition-colors">Terms &amp; Privacy</button>
            <a href={contactMailto("Vintage Vantage — hello")} data-testid="footer-contact-link" className="hover:text-[#B47A3C] transition-colors">Contact us</a>
            <button onClick={() => navigate("/login")} className="hover:text-[#B47A3C] transition-colors">Sign in</button>
            <button onClick={() => navigate("/register")} className="hover:text-[#B47A3C] transition-colors">Get started</button>
          </div>
        </div>
        <div className="border-t border-[#FDFBF7]/10">
          <div className="max-w-6xl mx-auto px-6 py-5">
            <p className="text-sm text-[#B8B8B8] leading-relaxed max-w-4xl" data-testid="footer-disclaimer">
              Vintage Vantage is a listing and organization tool. We don't guarantee marketplace approval, don't handle
              shipping or handling (that stays with the platform you sell on), and don't promise a sale. Platform policies,
              commissions and fees change — always verify current terms on each marketplace before listing.
            </p>
          </div>
        </div>
      </footer>

      <SubmitFlipDialog open={showSubmit} onClose={() => setShowSubmit(false)} onSubmitted={loadFlips} />
      <ShareFlipDialog story={shareStory} onClose={() => setShareStory(null)} />
      <StoryTour open={showTour} onClose={() => setShowTour(false)} onFinishCta={startCta} />
    </div>
  );
}
